/**
 * Utility functions for formatting message content
 * Handles both plain strings and structured JSON objects from the backend
 */

/**
 * Formats message content, handling both plain strings and structured JSON objects.
 * 
 * When the backend sends structured data like:
 * { "intro": "Hej.", "besked": "...", "spørgsmål": "..." }
 * 
 * This function converts it to readable markdown text.
 * 
 * @param content - Raw message content (string or object)
 * @returns Formatted string suitable for display
 */
export function formatMessageContent(content: string | Record<string, unknown> | undefined): string {
  if (!content) return '';
  
  // If it's already a string, check if it's JSON that needs parsing
  if (typeof content === 'string') {
    // Try to detect if it's a JSON string
    const trimmed = content.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'object' && parsed !== null) {
          return formatStructuredMessage(parsed as Record<string, unknown>);
        }
      } catch {
        // Not valid JSON, return as-is
        return content;
      }
    }
    return content;
  }
  
  // It's an object, format it
  if (typeof content === 'object' && content !== null) {
    return formatStructuredMessage(content);
  }
  
  return String(content);
}

/**
 * Converts a structured message object into readable markdown text.
 * Concatenates all string values with proper spacing.
 * 
 * @param obj - Structured message object
 * @returns Formatted markdown string
 */
export function formatStructuredMessage(obj: Record<string, unknown>): string {
  const parts: string[] = [];
  
  // Process values in order, concatenating string values
  for (const value of Object.values(obj)) {
    if (typeof value === 'string' && value.trim()) {
      parts.push(value.trim());
    } else if (Array.isArray(value)) {
      // Handle arrays (e.g., list items)
      const items = value
        .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
        .map(item => `- ${item.trim()}`);
      if (items.length > 0) {
        parts.push(items.join('\n'));
      }
    } else if (typeof value === 'object' && value !== null) {
      // Recursively handle nested objects
      const nested = formatStructuredMessage(value as Record<string, unknown>);
      if (nested) {
        parts.push(nested);
      }
    }
  }
  
  // Join with double newlines for paragraph separation
  return parts.join('\n\n');
}

/**
 * Extracts raw message content from event data
 * 
 * @param data - Event data object
 * @returns Raw message content (string or object)
 */
export function extractRawMessage(data: Record<string, unknown>): string | Record<string, unknown> | undefined {
  return data?.message as string | Record<string, unknown> | undefined;
}
